import { useEffect } from 'react'
import * as Sentry from '@sentry/nextjs'
import { i18n } from 'next-i18next'
import { useRouter } from 'next/router'
import useSWR, { KeyedMutator } from 'swr'
import { DISABLED_PAGE_URL } from 'shared/constants'
import { UserRolesEnum } from 'shared/enums/user-roles-enum'
import { changeLocale } from 'shared/i18n/change-locale'
import { resolveLocaleInPath } from 'shared/utils/resolve-locale-in-path'
import { UnauthorizedResponse } from '../errors/unauthorized-response'
import { UnauthorizedUser, UserInterface } from '../types/user-interface'
import { useApiWrapperWithErrorValidation } from './use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from './use-api-wrapper-with-error-validation/types'

export function isAuthorizedUser(user?: UserInterface | UnauthorizedUser): user is UserInterface {
  return user !== undefined && 'id' in user
}

export function isNotDomainOwner(user: UserInterface | UnauthorizedUser): boolean {
  return !isAuthorizedUser(user) || (isAuthorizedUser(user) && !user.isDomainOwner)
}

export function isDomainOwner(user: UserInterface | UnauthorizedUser): boolean {
  return isAuthorizedUser(user) && user.isDomainOwner
}

export function isCustomer(user: UserInterface | UnauthorizedUser): boolean {
  return (isAuthorizedUser(user) && user?.roles.includes(UserRolesEnum.Customer)) || false
}

export function isAffiliate(user: UserInterface | UnauthorizedUser): boolean {
  return (isAuthorizedUser(user) && user?.roles.includes(UserRolesEnum.Affiliate)) || false
}

export function isWorkspaceOwner(user?: UserInterface | UnauthorizedUser): boolean {
  return !!user && isAuthorizedUser(user) ? user.workspaceOwner === null : false
}

export function isWsAssistant(user?: UserInterface | UnauthorizedUser): boolean {
  if (user && isAuthorizedUser(user) && !isWorkspaceOwner(user) && user) {
    const workspace = user.workspaceMemberships?.find(el => el.ownerId === user.workspaceOwner)
    return workspace ? workspace.role === 'role_assistant' : false
  } else {
    return false
  }
}

export function isWsAdmin(user?: UserInterface | UnauthorizedUser): boolean {
  if (user && isAuthorizedUser(user) && !isWorkspaceOwner(user) && user) {
    const workspace = user.workspaceMemberships?.find(el => el.ownerId === user.workspaceOwner)
    return workspace ? workspace.role === 'role_admin' : false
  } else {
    return false
  }
}

export function isWorkspaceMember(user: UserInterface | UnauthorizedUser): boolean {
  return isAuthorizedUser(user) && (isDomainOwner(user) || isWsAdmin(user) || isWsAssistant(user))
}

export const USER_DATA_API = '/api/user/user-data'

interface UseUserResult {
  loading: boolean
  user: UnauthorizedUser | UserInterface | undefined
  mutate: KeyedMutator<UnauthorizedUser | UserInterface>
  isOwner: boolean | undefined
  isAccessRestricted: boolean
}

export default function useUser(
  options: {
    unauthorizedResponseHandler?: (error: UnauthorizedResponse) => void
  } = {},
): UseUserResult {
  const router = useRouter()

  const isCommunityLoginPage = router.pathname.endsWith('/login')
  const { fetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.get,
    UnauthorizedUser | UserInterface
  >({
    method: RequestMethodsEnum.get,
    unauthorizedResponseHandler:
      options.unauthorizedResponseHandler ?? (isCommunityLoginPage ? () => {} : undefined),
  })
  const { data, mutate, isValidating } = useSWR(USER_DATA_API, fetcher, {
    onSuccess: user => {
      if (!isAuthorizedUser(user)) return

      const isDisabled = user ? !user.impersonator && !user.enabled : false
      if (isDisabled && router.pathname !== DISABLED_PAGE_URL) {
        router.replace(DISABLED_PAGE_URL)
      }
    },
  })

  useEffect(() => {
    if (!data) return

    const { localeInPath, hasLocaleInPath } = resolveLocaleInPath(globalThis.location.pathname)
    const targetLocale = hasLocaleInPath ? (localeInPath ?? router.locale) : data.dashboardLocale

    if (targetLocale && targetLocale !== i18n?.language) {
      const applyLocale = async (): Promise<void> => {
        try {
          await changeLocale({
            i18n,
            locale: targetLocale,
            userDashboardLocale: data.dashboardLocale,
            revalidateUser: true,
            router,
          })
        } catch (error) {
          Sentry.captureException(error, {
            level: 'warning',
            extra: { targetLocale },
          })
        }
      }

      void applyLocale()
    }

    if (isAuthorizedUser(data)) {
      Sentry.setUser({
        id: String(data.id),
        username: `${data.firstName} ${data.lastName}`,
      })
    } else {
      Sentry.setUser(null)
    }
  }, [data, router])

  const isAccessRestricted = isAuthorizedUser(data) && data.accessRestricted
  const isOwner = data && isDomainOwner(data)

  return {
    loading: isValidating,
    user: data,
    mutate,
    isOwner,
    isAccessRestricted,
  }
}
